import {
  GithubIntegrationConfig,
  ScmIntegrationRegistry,
} from '@backstage/integration';
import parseGitUrl from 'git-url-parse';
import { ScmAuthApi } from '@backstage/integration-react';
import { Octokit } from '@octokit/rest';
import { getBranchName, getCatalogFilename } from '../components/helpers';
import { ConfigApi } from '@backstage/core-plugin-api';
import { Base64 } from 'js-base64';

declare module 'git-url-parse' {
  interface GitUrlParseResult {
    protocols: string[];
    protocol: string;
    port: string | null;
    resource: string;
    user: string;
    pathname: string;
    hash: string;
    search: string;
    href: string;
    token: string;
    toString: () => string;
    source: string;
    name: string;
    owner: string;
    organization: string | null;
    full_name: string;
    ref: string;
    filepathtype: string;
    filepathtypeOriginal: string;
    filepath: string;
    git_suffix: boolean;
    [key: string]: any;
  }

  function parseGitUrl(url: string): GitUrlParseResult;
}
export interface GitHubOptions {
  owner: string;
  repo: string;
  title: string;
  body: string;
  fileContent: string;
  repositoryUrl: string;
  githubIntegrationConfig: GithubIntegrationConfig;
}
export const getGithubIntegrationConfig = (
  scmIntegrationsApi: ScmIntegrationRegistry,
  location: string,
) => {
  const integration = scmIntegrationsApi.github.byUrl(location);
  if (!integration) {
    return undefined;
  }

  const { name: repo, owner } = parseGitUrl(location);
  return {
    repo,
    owner,
    githubIntegrationConfig: integration.config,
  };
};

export const REGISTRATION_ISSUE_TITLE =
  'Register repository in Backstage. Generate catalog-info.yaml file';
export const REGISTRATION_ISSUE_LABELS = ['backstage', 'automation'];

/**
 * Returns the default branch and whether a registration is already in
 * progress: either the integration branch exists (manual PR flow) or an open
 * Copilot registration issue exists. When possible it also returns the open
 * Pull Request so the caller can surface a "PR already open" message.
 */
export async function getRegistrationStatus(
  options: {
    owner: string;
    repo: string;
    branchName: string;
    repositoryUrl: string;
    githubIntegrationConfig: GithubIntegrationConfig;
  },
  scmAuthApi: ScmAuthApi,
): Promise<{
  defaultBranch: string;
  isRegistrationInProgress: boolean;
  existingPrUrl?: string;
  openIssueUrl?: string;
}> {
  const { owner, repo, branchName, repositoryUrl, githubIntegrationConfig } = options;
  const { token } = await scmAuthApi.getCredentials({
    url: repositoryUrl,
    additionalScope: { repoWrite: true },
  });
  const octo = new Octokit({ auth: token, baseUrl: githubIntegrationConfig.apiBaseUrl });

  const { data: repoData } = await octo.repos.get({ owner, repo }).catch(e => {
    throw new Error(formatHttpErrorMessage("Couldn't fetch repository data", e));
  });
  const defaultBranch = repoData.default_branch;

  let branchExists = false;
  let existingPrUrl: string | undefined;
  let openIssueUrl: string | undefined;

  // Helper: find an open PR for a given branch and return its URL.
  const findOpenPrUrl = async (head: string): Promise<string | undefined> => {
    const { data: prs } = await octo.pulls.list({
      owner,
      repo,
      head: `${owner}:${head}`,
      state: 'open',
    });
    return prs.length > 0 ? prs[0].html_url : undefined;
  };

  // 1. Check the canonical integration branch (e.g. "backstage-integration").
  try {
    await octo.git.getRef({ owner, repo, ref: `heads/${branchName}` });
    branchExists = true;
    existingPrUrl = await findOpenPrUrl(branchName);
  } catch (e: any) {
    if (e.status !== 404) {
      throw new Error(
        formatHttpErrorMessage(
          `Couldn't check if branch '${branchName}' already exists`,
          e,
        ),
      );
    }
  }

  // 2. Copilot picks its own branch name (pushing to a name we choose is
  //    rejected with a 403), so detect Copilot registrations by the open issue
  //    Backstage created instead of by branch name.
  if (!branchExists) {
    const [registrationIssue] = await octo
      .paginate(
        octo.issues.listForRepo,
        {
          owner,
          repo,
          state: 'open',
          labels: REGISTRATION_ISSUE_LABELS.join(','),
          per_page: 100,
        },
        (response, done) => {
          // The issues endpoint also returns pull requests; only real issues count.
          const match = response.data.find(
            issue =>
              !issue.pull_request && issue.title === REGISTRATION_ISSUE_TITLE,
          );
          if (match) done();
          return match ? [match] : [];
        },
      )
      .catch(e => {
        throw new Error(
          formatHttpErrorMessage(
            "Couldn't check for an open registration issue",
            e,
          ),
        );
      });
    if (registrationIssue) {
      openIssueUrl = registrationIssue.html_url;
      existingPrUrl = await findOpenLinkedPrUrl(
        octo,
        owner,
        repo,
        registrationIssue.number,
      );
    }
  }

  return {
    defaultBranch,
    isRegistrationInProgress: branchExists || openIssueUrl !== undefined,
    existingPrUrl,
    openIssueUrl,
  };
}

/**
 * Finds an open Pull Request that references the given issue (Copilot links
 * its PR to the issue it was assigned). Returns undefined when none is found.
 */
async function findOpenLinkedPrUrl(
  octo: Octokit,
  owner: string,
  repo: string,
  issueNumber: number,
): Promise<string | undefined> {
  try {
    const [linkedPrUrl] = await octo.paginate(
      octo.issues.listEventsForTimeline,
      { owner, repo, issue_number: issueNumber, per_page: 100 },
      (response, done) => {
        const linkedPrEvent = response.data.find(
          (event: any) =>
            event.event === 'cross-referenced' &&
            event.source?.issue?.pull_request &&
            event.source.issue.state === 'open',
        ) as any;
        if (linkedPrEvent) done();
        return linkedPrEvent ? [linkedPrEvent.source.issue.html_url as string] : [];
      },
    );
    return linkedPrUrl;
  } catch (e: any) {
    // Non-fatal: the open issue alone is enough to block a duplicate
    // registration; the PR link only improves the message shown to the user.
    // eslint-disable-next-line no-console
    console.warn(
      '[catalog-import] Could not look up the PR linked to the registration issue:',
      e.message,
    );
    return undefined;
  }
}

export async function submitGitHubPrToRepo(
  options: GitHubOptions,
  scmAuthApi: ScmAuthApi,
  configApi: ConfigApi,
): Promise<{ link: string; location: string }> {
  const {
    owner,
    repo,
    title,
    body,
    fileContent,
    repositoryUrl,
    githubIntegrationConfig,
  } = options;

  const { token } = await scmAuthApi.getCredentials({
    url: repositoryUrl,
    additionalScope: {
      repoWrite: true,
    },
  });

  const octo = new Octokit({
    auth: token,
    baseUrl: githubIntegrationConfig.apiBaseUrl,
  });

  const branchName = getBranchName(configApi);
  const fileName = getCatalogFilename(configApi);

  const repoData = await octo.repos
    .get({
      owner,
      repo,
    })
    .catch(e => {
      throw new Error(formatHttpErrorMessage("Couldn't fetch repo data", e));
    });

  const parentRef = await octo.git
    .getRef({
      owner,
      repo,
      ref: `heads/${repoData.data.default_branch}`,
    })
    .catch(e => {
      throw new Error(
        formatHttpErrorMessage("Couldn't fetch default branch data", e),
      );
    });

  await octo.git
    .createRef({
      owner,
      repo,
      ref: `refs/heads/${branchName}`,
      sha: parentRef.data.object.sha,
    })
    .catch(async e => {
      if (e.status === 422) {
        // Branch already exists — find the open PR if there is one
        let existingPrUrl: string | undefined;
        try {
          const { data: prs } = await octo.pulls.list({
            owner,
            repo,
            head: `${owner}:${branchName}`,
            state: 'open',
          });
          if (prs.length > 0) {
            existingPrUrl = prs[0].html_url;
          }
        } catch {
          // Ignore PR-lookup errors — fall through to generic message
        }
        if (existingPrUrl) {
          throw new Error(
            `A Pull Request is already open for this repository: ${existingPrUrl}`,
          );
        }
        throw new Error(
          `A registration is already in progress — branch '${branchName}' already exists. ` +
            `Please check your repository's open Pull Requests.`,
        );
      }
      throw new Error(
        formatHttpErrorMessage(
          `Couldn't create a new branch with name '${branchName}'`,
          e,
        ),
      );
    });

  await octo.repos
    .createOrUpdateFileContents({
      owner,
      repo,
      path: fileName,
      message: title,
      content: Base64.encode(fileContent),
      branch: branchName,
    })
    .catch(e => {
      throw new Error(
        formatHttpErrorMessage(
          `Couldn't create a commit with ${fileName} file added`,
          e,
        ),
      );
    });

  const pullRequestResponse = await octo.pulls
    .create({
      owner,
      repo,
      title,
      head: branchName,
      body,
      base: repoData.data.default_branch,
    })
    .catch(e => {
      throw new Error(
        formatHttpErrorMessage(
          `Couldn't create a pull request for ${branchName} branch`,
          e,
        ),
      );
    });

  return {
    link: pullRequestResponse.data.html_url,
    location: `https://${githubIntegrationConfig.host}/${owner}/${repo}/blob/${repoData.data.default_branch}/${fileName}`,
  };
}

export async function addTopicToRepo(
  options: {
    owner: string;
    repo: string;
    topic: string;
    repositoryUrl: string;
    githubIntegrationConfig: GithubIntegrationConfig;
  },
  scmAuthApi: ScmAuthApi,
): Promise<{ repoUrl: string }> {
  const { owner, repo, topic, repositoryUrl, githubIntegrationConfig } =
    options;

  const { token } = await scmAuthApi.getCredentials({
    url: repositoryUrl,
    additionalScope: {
      repoWrite: true,
    },
  });

  const octo = new Octokit({
    auth: token,
    baseUrl: githubIntegrationConfig.apiBaseUrl,
  });

  // Fetch existing topics so we can append without overwriting
  const { data: currentTopics } = await octo.repos
    .getAllTopics({ owner, repo })
    .catch(e => {
      throw new Error(
        formatHttpErrorMessage("Couldn't fetch repository topics", e),
      );
    });

  const existingNames: string[] = currentTopics.names ?? [];

  if (!existingNames.includes(topic)) {
    await octo.repos
      .replaceAllTopics({
        owner,
        repo,
        names: [...existingNames, topic],
      })
      .catch(e => {
        throw new Error(
          formatHttpErrorMessage(
            `Couldn't add topic '${topic}' to the repository`,
            e,
          ),
        );
      });
  }

  return {
    repoUrl: `https://${githubIntegrationConfig.host}/${owner}/${repo}`,
  };
}

/**
 * Fetches all topics from a GitHub repository.
 */
export async function getRepoTopics(
  options: {
    owner: string;
    repo: string;
    repositoryUrl: string;
    githubIntegrationConfig: GithubIntegrationConfig;
  },
  scmAuthApi: ScmAuthApi,
): Promise<string[]> {
  const { owner, repo, repositoryUrl, githubIntegrationConfig } = options;

  const { token } = await scmAuthApi.getCredentials({
    url: repositoryUrl,
  });

  const octo = new Octokit({
    auth: token,
    baseUrl: githubIntegrationConfig.apiBaseUrl,
  });

  const { data } = await octo.repos
    .getAllTopics({ owner, repo })
    .catch(e => {
      throw new Error(
        formatHttpErrorMessage("Couldn't fetch repository topics", e),
      );
    });

  return data.names ?? [];
}

const OWNER_TEAM_PERMISSIONS = ['admin', 'maintain'] as const;
type OwnerTeamPermission = (typeof OWNER_TEAM_PERMISSIONS)[number];

/**
 * Returns the team's owner-level permission, if any. GitHub can report a
 * maintain team as `permission: "push"` while `permissions.maintain` is true,
 * so the `permissions` flags take precedence over the `permission` string.
 */
function resolveOwnerTeamPermission(team: {
  permission: string;
  permissions?: Partial<Record<string, boolean>>;
}): OwnerTeamPermission | undefined {
  if (team.permissions) {
    return OWNER_TEAM_PERMISSIONS.find(
      permission => team.permissions?.[permission],
    );
  }
  return OWNER_TEAM_PERMISSIONS.find(
    permission => permission === team.permission,
  );
}

/**
 * Returns the slugs of the GitHub teams with admin or maintain access to the
 * repository, admin teams first.
 */
export async function getRepoOwnerTeams(
  options: {
    owner: string;
    repo: string;
    repositoryUrl: string;
    githubIntegrationConfig: GithubIntegrationConfig;
  },
  scmAuthApi: ScmAuthApi,
): Promise<string[]> {
  const { owner, repo, repositoryUrl, githubIntegrationConfig } = options;

  const { token } = await scmAuthApi.getCredentials({
    url: repositoryUrl,
  });

  const octo = new Octokit({
    auth: token,
    baseUrl: githubIntegrationConfig.apiBaseUrl,
  });

  const teams = await octo
    .paginate(
      octo.repos.listTeams,
      { owner, repo, per_page: 100 },
      response => response.data,
    )
    .catch(e => {
      throw new Error(
        formatHttpErrorMessage("Couldn't fetch repository teams", e),
      );
    });

  return teams
    .flatMap(team => {
      const permission = resolveOwnerTeamPermission(team);
      return permission ? [{ slug: team.slug, permission }] : [];
    })
    .sort(
      (a, b) =>
        OWNER_TEAM_PERMISSIONS.indexOf(a.permission) -
        OWNER_TEAM_PERMISSIONS.indexOf(b.permission),
    )
    .map(team => team.slug);
}

/**
 * Creates a GitHub Issue assigned to the Copilot SWE Agent.
 * Uses the user's OAuth token with a special GraphQL-Features header
 * to enable Copilot assignment.
 */
export async function createIssueWithCopilotAgent(
  options: {
    owner: string;
    repo: string;
    title: string;
    body: string;
    labels?: string[];
    repositoryUrl: string;
    githubIntegrationConfig: GithubIntegrationConfig;
  },
  scmAuthApi: ScmAuthApi,
): Promise<{ issueUrl: string; issueNumber: number }> {
  const {
    owner,
    repo,
    title,
    body,
    labels = REGISTRATION_ISSUE_LABELS,
    repositoryUrl,
    githubIntegrationConfig,
  } = options;

  const { token } = await scmAuthApi.getCredentials({
    url: repositoryUrl,
    additionalScope: { repoWrite: true },
  });

  const octo = new Octokit({
    auth: token,
    baseUrl: githubIntegrationConfig.apiBaseUrl,
    headers: {
      'GraphQL-Features': 'issues_copilot_assignment_api_support',
    },
  });

  const { data: createdIssue } = await octo.issues
    .create({
      owner,
      repo,
      title,
      body,
      labels,
      assignees: ['copilot-swe-agent[bot]'],
    })
    .catch(e => {
      throw new Error(
        formatHttpErrorMessage("Couldn't create issue with Copilot agent", e),
      );
    });

  return {
    issueUrl: createdIssue.html_url,
    issueNumber: createdIssue.number,
  };
}

/**
 * Returns true if the specified file already exists in the repository.
 * A 404 response means the file is absent; any other error is re-thrown.
 */
export async function checkCatalogFileExists(
  options: {
    owner: string;
    repo: string;
    filename: string;
    repositoryUrl: string;
    githubIntegrationConfig: GithubIntegrationConfig;
  },
  scmAuthApi: ScmAuthApi,
): Promise<boolean> {
  const { owner, repo, filename, repositoryUrl, githubIntegrationConfig } =
    options;

  const { token } = await scmAuthApi.getCredentials({ url: repositoryUrl });
  const octo = new Octokit({
    auth: token,
    baseUrl: githubIntegrationConfig.apiBaseUrl,
  });

  try {
    await octo.repos.getContent({ owner, repo, path: filename });
    return true;
  } catch (e: any) {
    if (e.status === 404) {
      return false;
    }
    throw new Error(
      formatHttpErrorMessage(
        `Couldn't check if ${filename} exists in the repository`,
        e,
      ),
    );
  }
}

function formatHttpErrorMessage(
  message: string,
  error: { status: number; message: string },
) {
  return `${message}, received http response status code ${error.status}: ${error.message}`;
}